import React, { useState } from 'react'
import useSWRMutation from 'swr/mutation'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'

import classNames from 'classnames'
import {
  ContentType,
  LessonData,
  TrialLessonIndexView,
} from '../../types/trialLessons'

import { Status } from './Status'
import { DAY_OF_WEEKS } from '../../types/date'
import { ChangeWeek } from './ChangeWeek'
import utc from 'dayjs/plugin/utc'
import { useCurrentStep } from '../../hooks/useCurrentStep'

type ItemProps = {
  setSelectedLesson: (arg: TrialLessonIndexView) => void
  lessonData?: LessonData
  isRescheduled?: boolean
  currentReservedLesson?: TrialLessonIndexView
  getNextWeekLessonData?: () => void
  getPreviousWeekLessonData?: () => void
  contentType?: ContentType
}

dayjs.extend(utc)
dayjs.extend(timezone)

export const TrialLessonCalendar = ({
  setSelectedLesson,
  lessonData,
  isRescheduled,
  currentReservedLesson,
  getNextWeekLessonData,
  getPreviousWeekLessonData,
  contentType,
}: ItemProps) => {
  async function getLessonInfo(url: string, { arg }: { arg: { id: string } }) {
    return fetch(`${url}${arg.id}`, {
      method: 'GET',
    }).then(res => res.json())
  }

  const { setCurrentStep } = useCurrentStep()

  const { trigger } = useSWRMutation(
    `${process.env.NEXT_PUBLIC_API_URL}/api/commons/v1/trial_lessons/`,
    getLessonInfo
  )
  const [week, setWeek] = useState(0)

  if (!lessonData) {
    return null
  }

  return (
    <>
      {getNextWeekLessonData && getPreviousWeekLessonData && (
        <ChangeWeek
          getNextWeekLessonData={getNextWeekLessonData}
          getPreviousWeekLessonData={getPreviousWeekLessonData}
          week={week}
          setWeek={(updateWeek: number) => {
            setWeek(updateWeek)
          }}
        />
      )}

      <div>
        <div className="sticky top-0 border border-input-border border-r-0 bg-white">
          <div className="border-b border-input-border border-r flex">
            <div className="w-[60px]"></div>
            {Object.keys(lessonData).map((item, i) => {
              const targetMonth = new Date(
                Object.keys(lessonData)[i - 1]
              ).getMonth()
              const nextTargetMonth = new Date(
                Object.keys(lessonData)[i]
              ).getMonth()
              // カレンダー表示が同じ月の場合は最初に生成したDOM or
              // 月末の表示で次の月が必要な場合はそれを生成する
              if (i === 0 || targetMonth !== nextTargetMonth) {
                return (
                  <div
                    key={item}
                    className="flex-1 text-center"
                    style={{
                      flex:
                        i === 0
                          ? 1
                          : (Object.keys(lessonData).length - i + 1) / (i + 1),
                    }}
                  >
                    {new Date(item).getMonth() + 1}月
                  </div>
                )
              }
            })}
          </div>
          <div className="flex">
            <div className="w-[60px] border-r border-input-border text-sm text-center p-3 leading-tight">
              開始日時
            </div>
            {Object.keys(lessonData).map(item => {
              const targetDate = dayjs(item).tz('Asia/Tokyo')

              const dayNum = targetDate.day()
              const date = targetDate.date()
              const dayOfWeek = DAY_OF_WEEKS[dayNum]

              const isSaturday = dayNum === 6
              const isSunday = dayNum === 0
              const isHoliday = lessonData[item].is_holiday
              return (
                <div
                  className={classNames(
                    'border-r border-input-border flex-1 text-[10px] flex items-center justify-center',
                    {
                      'text-pink-darken': isHoliday || isSunday,
                      'text-blue': isSaturday,
                    }
                  )}
                  key={item}
                >
                  <div>
                    <div className="text-center">{dayOfWeek}</div>
                    <div className="text-sm block font-bold">{date}</div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
        <div className="flex">
          <div className="text-center border-l border-input-border">
            {Object.keys(lessonData[Object.keys(lessonData)[0]].slots).map(
              row => (
                <div
                  className="border-r border-input-border border-b border-solid font-bold text-xs leading-[40px] h-[40px] w-[60px]"
                  key={row}
                >
                  {row}
                </div>
              )
            )}
          </div>

          {Object.keys(lessonData).map(date => {
            const { slots } = lessonData[date]
            return (
              <div key={date} className="flex-1">
                {Object.keys(slots)?.map(slot => {
                  const { id, status, reservable_count } = slots[slot]
                  const isReserved = id === currentReservedLesson?.id
                  const lessonId = id.toString()
                  return (
                    <div
                      className={`relative border-r border-b border-input-border leading-[40px] h-[40px] flex justify-center ${
                        reservable_count === 1 ? 'items-start' : 'items-center'
                      }`}
                      key={`${date}-${slot}`}
                    >
                      {status === 'reservable' && !isReserved ? (
                        <button
                          type="button"
                          className="w-full"
                          onClick={async () => {
                            // It uses only shares page
                            setCurrentStep(2)
                            try {
                              const result = await trigger({ id: lessonId })
                              setSelectedLesson(result.body)
                              setTimeout(() => {
                                window.scroll({
                                  top: (
                                    window.document.querySelector(
                                      '#step2'
                                    ) as HTMLElement
                                  )?.offsetTop,
                                  behavior: 'smooth',
                                })
                              }, 0)
                            } catch (e) {
                              // Error handling
                            }
                          }}
                        >
                          <Status
                            status={status}
                            showLabel={
                              contentType !== 'onlyOns' &&
                              reservable_count === 1
                            }
                            reservableCount={
                              contentType !== 'onlyOns' ? reservable_count : 0
                            }
                          />
                        </button>
                      ) : (
                        <Status status={isReserved ? 'reserved' : status} />
                      )}
                    </div>
                  )
                })}
              </div>
            )
          })}
        </div>
      </div>
      {getNextWeekLessonData && getPreviousWeekLessonData && (
        <ChangeWeek
          getNextWeekLessonData={getNextWeekLessonData}
          getPreviousWeekLessonData={getPreviousWeekLessonData}
          week={week}
          setWeek={(updateWeek: number) => {
            setWeek(updateWeek)
          }}
        />
      )}
      <div className="mt-6 bg-bg-gray-0 py-3 pb-2">
        <dl className="bg-bg-gray-0">
          <div className="flex mb-1 items-center">
            <dt className="flex-1">
              <Status status="reservable" />
            </dt>
            <dd className="flex-[4] text-left text-sm leading-6">
              予約できます
            </dd>
          </div>
          <div className="flex mb-1 items-center">
            <dt className="flex-1">
              <Status status="reservable" reservableCount={1} />
            </dt>
            <dd className="flex-[4] text-left text-sm leading-6">
              残席がわずかです。ご予約をお急ぎください
            </dd>
          </div>
          <div className="flex mb-1 text-center">
            <dt className="flex-1">
              <Status status="full" />
            </dt>
            <dd className="flex-[4] text-left text-sm leading-6">
              予約できません。他の日時を選択してください
            </dd>
          </div>
          {isRescheduled && (
            <div className="flex mb-1 text-center">
              <dt className="flex-1">
                <Status status="reserved" />
              </dt>
              <dd className="flex-[4] text-left text-sm leading-6">
                すでにご予約済みです。他の日時を選択してください
              </dd>
            </div>
          )}
          <div className="flex mb-1 text-center">
            <dt className="flex-1">
              <Status status="none" />
            </dt>
            <dd className="flex-[4] text-left text-sm leading-6">
              開催枠がありません
            </dd>
          </div>
        </dl>
      </div>
    </>
  )
}
